import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { BPCDisplayConfigField } from '@core/interfaces/bpc-display-config';
import { get } from 'lodash';
import { CenterSetting } from '@core/interfaces/center-setting';

@Component
export default class AbstractDisplayField extends Vue {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Prop() readonly value: any;
  @Prop(Object) readonly fieldConfig: BPCDisplayConfigField;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Prop(Object) readonly item: any;
  @Prop({ type: Boolean, default: false }) readonly cancelledFilter: boolean;
  public allowedConditionalModel = ['item', 'centerSetting'];

  get type() {
    return this.fieldConfig?.displayType || null;
  }

  get background(): string {
    const backgroundColor = get(this.fieldConfig, 'backgroundColor');
    if (backgroundColor) {
      return backgroundColor;
    }

    const backgroundByField = get(this.fieldConfig, 'backgroundByField');
    if (backgroundByField) {
      return get(this.item, backgroundByField, null);
    }

    /**
     * 'backgroundByFieldValueToColor' => [
            'field' => 'status',
            'valueToColor' => [
                ['value' => 'active', 'color' => 'blue'],
                ['value' => 'inactive', 'color' => 'red'],
            ]
        ]
     */
    const backgroundByFieldValueToColor = get(this.fieldConfig, 'backgroundByFieldValueToColor');
    if (backgroundByFieldValueToColor) {
      const field = get(backgroundByFieldValueToColor, 'field');
      const value = get(this.item, field);
      const item = backgroundByFieldValueToColor.valueToColor?.find(item => item.value === value);
      return item?.color || null;
    } else {
      return null;
    }
  }

  get centerSetting(): CenterSetting {
    return this.$store.getters['centerSettings/centerSettings'];
  }

  get hideField(): boolean {
    let show = true;
    let hide = false;
    const showIfFieldPath = get(this.fieldConfig, 'conditionalShow.fieldPath', null);
    if (showIfFieldPath) {
      const showIfOperator = get(this.fieldConfig, 'conditionalShow.operator', '=');
      const showIfValue = get(this.fieldConfig, 'conditionalShow.value', null);
      const formModel = get(this.fieldConfig, 'conditionalShow.formModel', 'item');
      const showIfFormModel = this.allowedConditionalModel.includes(formModel) ? formModel : 'item';
      const modelValue = get(this, `${showIfFormModel}.${showIfFieldPath}`, null);
      if (!this.compare(showIfOperator, modelValue, showIfValue)) {
        show = false;
      }
    }

    const hideIfFieldPath = get(this.fieldConfig, 'conditionalHide.fieldPath', null);
    if (hideIfFieldPath) {
      const hideIfOperator = get(this.fieldConfig, 'conditionalHide.operator', '=');
      const hideIfValue = get(this.fieldConfig, 'conditionalHide.value', null);
      const formModel = get(this.fieldConfig, 'conditionalHide.formModel', 'item');
      const hideIfFormModel = this.allowedConditionalModel.includes(formModel) ? formModel : 'item';
      const modelValue = get(this, `${hideIfFormModel}.${hideIfFieldPath}`, null);
      if (this.compare(hideIfOperator, modelValue, hideIfValue)) {
        hide = true;
      }
    }

    return !show || hide;
  }

  get conditionalClass(): string {
    let className = null;
    const conditionalClassFieldPath = get(this.fieldConfig, 'conditionalClass.fieldPath', null);
    if (conditionalClassFieldPath) {
      const conditionalClassOperator = get(this.fieldConfig, 'conditionalClass.operator', null);
      const conditionalClassValue = get(this.fieldConfig, 'conditionalClass.value', null);
      const modelValue = get(this, 'value', conditionalClassFieldPath);
      if (this.compare(conditionalClassOperator, modelValue, conditionalClassValue)) {
        className = get(this.fieldConfig, 'conditionalClass.class', null);
      }
    }
    return className;
  }

  get emptyIfNull() {
    return this.fieldConfig?.onNullValue === 'empty';
  }

  get zeroIfNull() {
    return this.fieldConfig?.onNullValue === 'zero';
  }

  compare(operator, currentValue, targetValue) {
    switch (operator) {
      case '=':
        return currentValue == targetValue;
      case '!=':
        return currentValue != targetValue;
      case '>':
        return currentValue > targetValue;
      case '<':
        return currentValue < targetValue;
      case '>=':
        return currentValue >= targetValue;
      case '<=':
        return currentValue <= targetValue;
      case 'in':
        return targetValue.indexOf(currentValue) > -1;
      default:
        return false;
    }
  }
}
